import React, { useState, useEffect } from "react";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { getter } from "@progress/kendo-react-common";
import { useSelector } from "react-redux";
import useReloadButton from "../../../../../CustomHooks/useReloadButton";
import { useKendoGridHandlers } from "../../../../../CustomHooks/useKendoGridHandlers";
import { FetchData } from "../../../../../utils/array";
import { DisplayMsg } from "../../../../../services/toastService";

const DATA_ITEM_KEY = "id";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

export const DEFAULT_GRID_INFO_CLIENT_KIOSK = {
  action: "LOADJOTFORM",
  className: "PRACTICEDETAIL",
  pageSize: 20,
  currentPageNumber: 1,
  sortName: "FORM_NAME",
  sortOrder: "desc",
  gridHeight: "300px",
};

const PAGER_SETTINGS = {
  info: true,
  type: "input",
  previousNext: true,
  pageSize: 4,
  refresh: true,
};

const KendoKioskFormGrid = React.memo((props) => {
  const { entityId } = props;
  const sharedData = useSelector((state) => state.SharedLayoutData);
  const [gridData, setGridData] = useState([]);
  const [total, setTotal] = useState(0);
  const [reloadToggle, setReloadToggle] = useState(false);

  const [gridState, setGridState] = useState({
    pageNumber: DEFAULT_GRID_INFO_CLIENT_KIOSK.currentPageNumber,
    pageSize: DEFAULT_GRID_INFO_CLIENT_KIOSK.pageSize,
    sortName: DEFAULT_GRID_INFO_CLIENT_KIOSK.sortName,
    sortOrder: DEFAULT_GRID_INFO_CLIENT_KIOSK.sortOrder,
    sort: [
      {
        field: DEFAULT_GRID_INFO_CLIENT_KIOSK.sortName,
        dir: DEFAULT_GRID_INFO_CLIENT_KIOSK.sortOrder?.toLowerCase() || "asc",
      },
    ],
  });

  const { HandleDataStateChange, HandlePageChange } =
    useKendoGridHandlers(setGridState);

  const [selectedState, setSelectedState] = React.useState();

  // UseEffect to load the grid data
  useEffect(() => {
    LoadClientKioskGridData();
  }, [gridState, reloadToggle, props]);

  const LoadClientKioskGridData = async () => {
    document.getElementById("loader").style.display = "block";

    const queryParams = new URLSearchParams({
      facilityId:
        sharedData.currentFacilityId === 0 ? "" : sharedData.currentFacilityId,
    }).toString();

    const response = await FetchData(
      `Practice/${sharedData.currentEntityId}/kioskForm`,
      "PEHR",
      queryParams,
      sharedData.clientApiKey
    );

    if (response && response.succeeded) {
      setGridData(response.data.rows);
      setTotal(response.data.total);
    } else {
      setGridData([]);
      setTotal(0);
      DisplayMsg("2", response.Message);
    }
    document.getElementById("loader").style.display = "none";
  };

  useReloadButton(setReloadToggle);

  const SelectedContentLength = () => {
    const selectedText = window.getSelection().toString();
    return selectedText.length > 0;
  };

  const onSelectionChange = React.useCallback(
    (event) => {
      const check = SelectedContentLength();
      if (!check) {
        const newSelectedState = getSelectedState({
          event,
          selectedState,
          dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
      }
    },
    [selectedState]
  );

  const ShowKioskDetail = (data) => {
    props.ParentCallBack({
      ...props.State,
      formId: data.id,
      formName: data.formName,
      jotUrl: (data.filePath =
        data.filePath != null && data.filePath.includes("jotform.com")
          ? data.filePath
          : `https://form.jotform.com${data.filePath}`),
      documentType: data.documentType,
      optional: data.optional,
      jotField: data.jotFields,
      update: true,
    });
  };

  const KioskName = (props) => (
      <td>
        <span
          role="button"
          className="jotForm"
          title={`select to view detail of${  props.dataItem.formName}`}
          onClick={() => ShowKioskDetail(props.dataItem)}
        >
          {props.dataItem.formName}
        </span>
      </td>
    );

  return (
    <div>
      <Tooltip openDelay={100} position="right" anchorElement="target">
        <Grid
          style={{ height: "calc(100vh - 417px)" }}
          scrollable="scrollable"
          total={total}
          skip={(gridState.pageNumber - 1) * gridState.pageSize}
          pageSize={gridState.pageSize}
          filterable={false}
          sortable={{
            mode: "single",
            allowUnsort: false,
          }}
          sort={gridState.sort}
          selectedField={SELECTED_FIELD}
          data={gridData.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedState?.[idGetter(item)] || false,
          }))}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: "multiple",
          }}
          pageable={PAGER_SETTINGS}
          onPageChange={HandlePageChange}
          onDataStateChange={HandleDataStateChange}
          onSelectionChange={onSelectionChange}
        >
          <GridColumn
            title="Form Name"
            field="formName"
            cell={KioskName}
            width={150}
          />
          <GridColumn title="Jot Url" field="filePath" width={210} />
          <GridColumn title="Doc Type" field="documentType" width={210} />
          <GridColumn title="Optional" field="optional" width={210} />
          <GridColumn title="Jot Fields" field="jotFields" width={210} />
        </Grid>
      </Tooltip>
    </div>
  );
});
export default KendoKioskFormGrid;
